// frontend/src/components/ProductReviews.jsx
//
// Shows approved reviews for one product and lets a customer leave one.
//
// Reviews land in Supabase with approved = false and appear nowhere until you
// flip that flag in the Supabase dashboard. Nothing a stranger types reaches
// your structured data unread — which matters, because review markup is the
// kind Google issues manual actions over when it is wrong.
//
// The star rating in Google's results comes from prerender.js reading the same
// table at build time, so an approved review shows in search after the next
// deploy, not immediately. On the page itself it appears as soon as it is
// approved, because this component fetches at runtime.
//
// Review-request links: send customers the product URL ending in
// #customer-reviews. The page opens with the review form already open and the
// "Customer reviews" heading sitting just below the site header.

import { useState, useEffect, useCallback, useRef } from 'react';

import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../data/supabase';

const SUPABASE_KEY = SUPABASE_ANON_KEY;

const REVIEWS_ID = 'customer-reviews';
const REVIEWS_HASH = `#${REVIEWS_ID}`;

// sb_publishable_ keys are not JWTs. apikey header only — adding
// `Authorization: Bearer` makes Supabase reject the request.
const headers = {
  apikey: SUPABASE_KEY,
  'Content-Type': 'application/json',
};

// Height of whatever fixed or sticky bar is pinned to the top of the screen,
// measured live so it is right on mobile and desktop alike. Falls back to 72px
// if no pinned header is found.
function headerOffset() {
  let bottom = 0;
  document.querySelectorAll('header, nav').forEach((el) => {
    const pos = window.getComputedStyle(el).position;
    if (pos !== 'fixed' && pos !== 'sticky') return;
    const r = el.getBoundingClientRect();
    if (r.top <= 1 && r.bottom > bottom) bottom = r.bottom;
  });
  return (bottom || 72) + 12;
}

// Scrolls so the top of the reviews section sits just below the header.
// scrollIntoView ignores fixed headers, which is why the heading was hidden.
export function scrollToReviews(smooth = true) {
  const el = document.getElementById(REVIEWS_ID);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.scrollY - headerOffset();
  window.scrollTo({ top: Math.max(0, y), behavior: smooth ? 'smooth' : 'auto' });
}

function Stars({ value, onChange, size = 20, firstRef }) {
  const interactive = typeof onChange === 'function';
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          ref={n === 1 ? firstRef : undefined}
          type="button"
          disabled={!interactive}
          onClick={interactive ? () => onChange(n) : undefined}
          aria-label={interactive ? `${n} star${n > 1 ? 's' : ''}` : undefined}
          className={interactive ? 'cursor-pointer' : 'cursor-default'}
        >
          <svg
            viewBox="0 0 24 24"
            width={size}
            height={size}
            fill={n <= value ? '#f59e0b' : '#e5e7eb'}
            aria-hidden="true"
          >
            <path d="M12 2l2.9 6.1 6.6.9-4.8 4.7 1.2 6.6L12 17.2 6.1 20.3l1.2-6.6L2.5 9l6.6-.9z" />
          </svg>
        </button>
      ))}
    </span>
  );
}

// When the page is opened from a review-request link, keep the reviews section
// in place while the rest of the page loads. Images above it finish loading
// late and push it down, so the position is re-checked a few times. Stops the
// moment the customer scrolls or touches the screen themselves.
function useJumpToReviews(firstStarRef) {
  useEffect(() => {
    if (window.location.hash !== REVIEWS_HASH) return undefined;

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    let userMoved = false;
    const stop = () => {
      userMoved = true;
    };
    const passive = { passive: true };
    window.addEventListener('wheel', stop, passive);
    window.addEventListener('touchstart', stop, passive);
    window.addEventListener('keydown', stop);

    const go = () => {
      if (!userMoved) scrollToReviews(false);
    };

    const timers = [
      setTimeout(go, 100),
      setTimeout(() => {
        go();
        firstStarRef.current?.focus({ preventScroll: true });
      }, 500),
      setTimeout(go, 1200),
      setTimeout(go, 2500),
    ];
    window.addEventListener('load', go);

    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('wheel', stop, passive);
      window.removeEventListener('touchstart', stop, passive);
      window.removeEventListener('keydown', stop);
      window.removeEventListener('load', go);
    };
  }, [firstStarRef]);
}

export default function ProductReviews({ slug, productName }) {
  const cameForReview =
    typeof window !== 'undefined' && window.location.hash === REVIEWS_HASH;

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(cameForReview);
  const [rating, setRating] = useState(0);
  const [author, setAuthor] = useState('');
  const [text, setText] = useState('');
  const [status, setStatus] = useState(null); // null | 'sending' | 'done' | 'error'

  const firstStarRef = useRef(null);
  useJumpToReviews(firstStarRef);

  const load = useCallback(async () => {
    if (!SUPABASE_KEY || !slug) {
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/reviews` +
          `?select=author,rating,text,created_at` +
          `&page_slug=eq.${encodeURIComponent(slug)}` +
          `&approved=eq.true&order=created_at.desc&limit=20`,
        { headers: { apikey: SUPABASE_KEY } },
      );
      if (res.ok) setReviews(await res.json());
    } catch {
      // A failed read leaves the section empty. Not worth an error message —
      // the customer came here to buy an AC, not to read reviews.
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    if (!rating || !author.trim() || !text.trim()) return;
    setStatus('sending');
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/reviews`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          page_slug: slug,
          author: author.trim().slice(0, 60),
          rating,
          text: text.trim().slice(0, 1000),
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      setStatus('done');
      setRating(0);
      setAuthor('');
      setText('');
    } catch {
      setStatus('error');
    }
  };

  const count = reviews.length;
  const avg = count
    ? (reviews.reduce((s, r) => s + r.rating, 0) / count).toFixed(1)
    : null;

  return (
    <section id={REVIEWS_ID} className="mt-10 max-w-3xl border-t border-gray-200 pt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-900">Customer reviews</h2>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:border-blue-400 hover:text-blue-700"
        >
          {open ? 'Cancel' : 'Write a review'}
        </button>
      </div>

      {count > 0 && (
        <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
          <Stars value={Math.round(avg)} />
          <span className="font-medium text-gray-900">{avg}</span>
          <span>
            out of 5 · {count} review{count > 1 ? 's' : ''}
          </span>
        </div>
      )}

      {open && (
        <div className="mt-4 rounded-xl border border-gray-200 p-4">
          {status === 'done' ? (
            <p className="text-sm text-gray-700">
              Thank you. Your review has been submitted and will appear once we have
              checked it.
            </p>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                Reviewing {productName}. Published after a quick check.
              </p>
              <div className="mt-3">
                <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-400">
                  Rating
                </span>
                <Stars
                  value={rating}
                  onChange={setRating}
                  size={26}
                  firstRef={firstStarRef}
                />
              </div>
              <input
                type="text"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                placeholder="Your name"
                maxLength={60}
                autoComplete="name"
                className="mt-3 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="How is the unit? Cooling, installation, service — whatever would help the next buyer."
                rows={4}
                maxLength={1000}
                className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              {status === 'error' && (
                <p className="mt-2 text-sm text-red-600">
                  That did not save. Please try again, or WhatsApp us on 7703900307.
                </p>
              )}
              <button
                type="button"
                onClick={submit}
                disabled={!rating || !author.trim() || !text.trim() || status === 'sending'}
                className="mt-3 rounded-md bg-blue-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-40"
              >
                {status === 'sending' ? 'Submitting…' : 'Submit review'}
              </button>
            </>
          )}
        </div>
      )}

      <div className="mt-5 space-y-4">
        {loading && <p className="text-sm text-gray-400">Loading reviews…</p>}
        {!loading && count === 0 && !open && (
          <p className="text-sm text-gray-500">
            No reviews for this model yet. If you have bought one from us, yours would be
            the first.
          </p>
        )}
        {reviews.map((r, i) => (
          <div key={`${r.author}-${i}`} className="border-b border-gray-100 pb-4 last:border-0">
            <div className="flex flex-wrap items-center gap-2">
              <Stars value={r.rating} size={15} />
              <span className="text-sm font-medium text-gray-900">{r.author}</span>
              <span className="text-xs text-gray-400">
                {(r.created_at || '').split('T')[0]}
              </span>
            </div>
            <p className="mt-1.5 text-sm leading-relaxed text-gray-700">{r.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// Compact rating line for the top of the page, beside the price. Clicking it
// scrolls to the full section below.
//
// Renders nothing when there are no reviews. A product page showing "0 reviews"
// reads worse than one that never mentions reviews at all, and with 18 models
// most pages will sit at zero for a while.
export function ReviewSummary({ slug }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!SUPABASE_KEY || !slug) return;
    fetch(
      `${SUPABASE_URL}/rest/v1/reviews?select=rating&page_slug=eq.${encodeURIComponent(slug)}&approved=eq.true`,
      { headers: { apikey: SUPABASE_KEY } },
    )
      .then((r) => (r.ok ? r.json() : []))
      .then(setRows)
      .catch(() => setRows([]));
  }, [slug]);

  if (!rows || rows.length === 0) return null;

  const avg = (rows.reduce((s, r) => s + r.rating, 0) / rows.length).toFixed(1);

  return (
    <button
      type="button"
      onClick={() => scrollToReviews(true)}
      className="mt-2 inline-flex items-center gap-1.5 text-sm text-gray-600 transition hover:text-blue-700"
    >
      <Stars value={Math.round(avg)} size={16} />
      <span className="font-medium text-gray-900">{avg}</span>
      <span className="underline decoration-gray-300 underline-offset-2">
        {rows.length} review{rows.length > 1 ? 's' : ''}
      </span>
    </button>
  );
}